import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ChangeDetectorRef,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { PermissionService } from '@app/core/services/permission.service';

import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  FILTER_ACTIVE_PARAM,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { map } from 'rxjs/operators';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { SpuService, SearchFormData } from '../services/spu.service';
import { SpuCategoryService } from '../services/spu-category.service';
import { SpuBrandService } from '../services/spu-brand.service';
import { extractCategoryIdFromTreeselect } from '@shared/utils/treeselect.util';
import {
  OrdinarySpuItem,
  ORDINARY_SPU_SEARCH_CONFIG,
  ORDINARY_SPU_COLUMNS,
  ORDINARY_SPU_DEFAULT_COLUMNS_ORDER,
  ORDINARY_SPU_FLEXIBLE_COLUMNS_ORDER,
  ORDINARY_SPU_ACTION_COLUMN,
  ORDINARY_SPU_BUTTON_CONFIG,
} from '../spu.config';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';

@Component({
  selector: 'app-ordinary-spu',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    AdvancedDataTableComponent,
    AppButtonComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './ordinary-spu.component.html',
  styleUrl: './ordinary-spu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdinarySpuComponent implements OnInit, OnDestroy {
  // Configuration
  searchFormConfig = ORDINARY_SPU_SEARCH_CONFIG;
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product.approve');
  });

  // Check if user has any action permissions (CREATE, UPDATE, APPROVE, or DELETE)
  hasAnyActionPermission = computed(
    () =>
      this.canCreate() ||
      this.canUpdate() ||
      this.canApprove() ||
      this.canDelete(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ORDINARY_SPU_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of ORDINARY_SPU_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    // Show actions column if user has any permission (read, create, update, delete, or approve)
    if (this.hasAnyActionPermission() || this.canRead()) {
      const actionsCol = fromConfig(ORDINARY_SPU_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ORDINARY_SPU_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of ORDINARY_SPU_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Button configuration base
  private readonly primaryButtonBase = ORDINARY_SPU_BUTTON_CONFIG;

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedSpu: OrdinarySpuItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'disable' | 'enable' | 'delete' | null = null;

  // Data signals - following brand pattern
  spus = signal<OrdinarySpuItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    name: '',
    spuCode: '',
    type: '',
    categoryIdList: '' as string | string[],
    brandId: '',
    materialName: '',
    status: null as boolean | null,
    page: 1,
    size: 20,
  });

  // Private properties
  private destroy$ = new Subject<void>();
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private spuService: SpuService,
    private categoryService: SpuCategoryService,
    private brandService: SpuBrandService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.searchFormConfig = this.buildSearchFormConfig();
    this.loadCategoryTreeOptions();

    const urlParams = this.route.snapshot.queryParams;
    const hasSearchParams = urlParams[FILTER_ACTIVE_PARAM] === 'true';

    if (!hasSearchParams) {
      this.loadSpus();
    }

    this.setupRouterListener();

    // Subscribe to permissions loaded state to trigger change detection
    // This ensures computed signals re-evaluate when permissions are loaded
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (loaded) {
          // Trigger change detection to update computed signals
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Initialization methods
  private initializeComponent(): void {
    // Set SPU type for API calls
    this.spuService.setSpuType('ordinary');
  }

  private buildSearchFormConfig(): FormFieldConfig[] {
    return ORDINARY_SPU_SEARCH_CONFIG.map((field) => {
      if (field.key !== 'brandId') {
        return field;
      }

      return {
        ...field,
        type: 'searchableDropdown',
        showClear: true,
        paginatedOptions: {
          loadPage: ({ page, size, searchText }) =>
            this.brandService
              .getBrandOptionsPage({
                brandName: searchText || undefined,
                page,
                size,
              })
              .pipe(
                map((response) => ({
                  options: response.options,
                  pageContentLength: response.pageContentLength,
                  totalElements: response.totalElements,
                  hasNextPage: response.hasNextPage,
                })),
              ),
        },
      };
    });
  }

  private loadCategoryTreeOptions(): void {
    this.categoryService
      .getCategoryTree({
        statusFlag: undefined,
        subFlag: undefined,
        removeNotSubFlag: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (categoryTree) => {
          this.searchFormConfig = this.searchFormConfig.map((field) =>
            field.key === 'categoryIdList'
              ? ({ ...field, treeOptions: categoryTree } as FormFieldConfig)
              : field,
          );
        },
        error: (error) => {
          console.error('Error loading category tree:', error);
        },
      });
  }

  // Load SPUs - following brand pattern
  private loadSpus(): void {
    this.loading.set(true);
    this.spuService.getSpus(this.searchParams()).subscribe({
      next: (response) => {
        this.spus.set(response.data as OrdinarySpuItem[]);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading SPUs:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.spu.messages.error.failed_to_load_products',
          ),
        });
      },
    });
  }

  private setupRouterListener(): void {
    // Listen for navigation events to refresh data when returning to this page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        // Check if we're navigating to the ordinary SPU list page
        if (
          event.url.includes('/goods/spu/ordinary') &&
          !event.url.includes('/add') &&
          !event.url.includes('/edit') &&
          !event.url.includes('/view')
        ) {
          // Refresh data when returning to list page
          this.loadSpus();
        }
      });
  }

  onSearch(searchData: SearchFormData): void {
    // Extract category ID from treeselect value
    const extractedCategoryId = extractCategoryIdFromTreeselect(
      searchData.categoryIdList,
    );
    const processedSearchData = {
      ...searchData,
      categoryIdList: extractedCategoryId || '', // Convert null to empty string
    };

    this.searchParams.set({
      ...this.searchParams(),
      ...processedSearchData,
      page: 1,
    });
    this.loadSpus();
  }

  onReset(): void {
    this.searchParams.set({
      name: '',
      spuCode: '',
      type: '',
      categoryIdList: '' as string | string[],
      brandId: '',
      materialName: '',
      status: null,
      page: 1,
      size: 20,
    });
    this.loadSpus();
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1, // Advanced table uses 0-based, convert to 1-based
      size: event.rows,
    });
    this.loadSpus();
  }

  onAdd(): void {
    this.router.navigate(['/goods/spu/ordinary/add']);
  }

  onView(spu: OrdinarySpuItem): void {
    this.router.navigate(['/goods/spu/ordinary/view'], {
      queryParams: {
        id: spu.id,
        ...this.route.snapshot.queryParams,
      },
    });
  }

  onEdit(spu: OrdinarySpuItem): void {
    this.router.navigate(['/goods/spu/ordinary/edit'], {
      queryParams: {
        id: spu.id,
        ...this.route.snapshot.queryParams,
      },
    });
  }

  onDelete(spu: OrdinarySpuItem): void {
    this.selectedSpu = spu;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.spu.messages.confirmation.delete_product',
      { name: spu.name },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onDisable(spu: OrdinarySpuItem): void {
    this.selectedSpu = spu;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.spu.messages.confirmation.deactivate_product',
      { name: spu.name },
    );
    this.currentAction = 'disable';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('goods.spu.actions.deactivate'),
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onEnable(spu: OrdinarySpuItem): void {
    this.selectedSpu = spu;
    this.dialogTitle = this.translateService.instant(
      'common.confirmation.title',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.spu.messages.confirmation.activate_product',
      { name: spu.name },
    );
    this.currentAction = 'enable';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('goods.spu.actions.activate'),
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      this.executeConfirmedAction();
    }
  }

  private executeConfirmedAction(): void {
    if (!this.selectedSpu) return;

    switch (this.currentAction) {
      case 'disable':
        this.confirmDisable();
        break;
      case 'enable':
        this.confirmEnable();
        break;
      case 'delete':
        this.confirmDelete();
        break;
    }
  }

  private confirmDisable(): void {
    if (this.selectedSpu) {
      this.spuService
        .updateStatus(this.selectedSpu.id, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.spu.messages.success.product_deactivated',
              ),
            });
            this.loadSpus(); // Refresh data
          },
          error: (error) => {
            console.error('Error deactivating SPU:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.spu.messages.error.failed_to_update_status',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private confirmEnable(): void {
    if (this.selectedSpu) {
      this.spuService
        .updateStatus(this.selectedSpu.id, true)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.spu.messages.success.product_activated',
              ),
            });
            this.loadSpus(); // Refresh data
          },
          error: (error) => {
            console.error('Error activating SPU:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.spu.messages.error.failed_to_update_status',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private confirmDelete(): void {
    if (this.selectedSpu) {
      this.spuService
        .deleteSpu(this.selectedSpu.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (message) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.spu.messages.success.product_deleted',
              ),
            });
            this.loadSpus(); // Refresh data
          },
          error: (error) => {
            console.error('Error deleting SPU:', error);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.spu.messages.error.failed_to_delete_product',
              ),
            });
          },
        });
    }
    this.closeConfirmationDialog();
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedSpu = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  // Status methods
  getStatusSeverity(status: boolean): 'success' | 'secondary' {
    return status ? 'success' : 'secondary';
  }

  getStatusDisplayText(status: boolean): string {
    return status
      ? this.translateService.instant('common.table.status.active')
      : this.translateService.instant('common.table.status.inactive');
  }

  getTypeDisplayText(type: string): string {
    if (type === 'Physical Product') {
      return this.translateService.instant('goods.spu.types.physical_product');
    }
    return this.translateService.instant('goods.spu.types.virtual_product');
  }

  getStatusIcon(status: boolean): string {
    return status
      ? 'pi pi-check-circle text-green-500'
      : 'pi pi-times-circle text-gray-500';
  }

  getStatusStyle(status: boolean): any {
    return status
      ? {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        }
      : {
          'background-color': '#f9fafb',
          color: '#6b7280',
          border: 'none',
        };
  }
}
